// Character Studio transport.
//
// Same-origin by design: the base is empty, every path is `/v1/...`, and the
// container serving this SPA proxies `/v1` to the API preserving Host. That is
// why no build ever bakes a hostname and why there is no CORS story to get
// wrong.
//
// One envelope parser, one place. Callers get `ApiError` with a stable machine
// `code` and never have to reach into a Response.

/** Empty by design: the SPA is served same-origin with /v1/*. */
const API_BASE = "";
let unauthorizedHandler: (() => void) | undefined;

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

export interface ApiErrorInit {
  status: number;
  code: string;
  message: string;
  requestId?: string;
  details?: unknown;
}

/**
 * A rejected API call.
 *
 * `code` is the contract, `message` is diagnostics. Generic messages are not
 * member-facing copy. Only `memberRefusal` reads the API's explicitly
 * curated, closed-vocabulary member-refusal detail.
 *
 * `details` stays `unknown` on purpose. It is unvalidated server data, and
 * typing it as a record here would hand every call site a shape nobody
 * checked.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * The field a `validation_failed` rejection blames, if it named one.
 *
 * This reader narrows validation_failed details to a field identifier. It does
 * not authorize arbitrary diagnostics as member copy. Explicit member_refused
 * details use the separate closed boundary below.
 */
export function validationField(error: unknown): string | undefined {
  if (!isApiError(error) || error.code !== "validation_failed")
    return undefined;
  const { details } = error;
  if (typeof details !== "object" || details === null) return undefined;
  const field = "field" in details ? details.field : undefined;
  return typeof field === "string" && field !== "" ? field : undefined;
}

const MEMBER_REFUSAL_REASONS: Readonly<Record<string, true>> = {
  content_restriction: true,
  line_too_long: true,
  no_pose_match: true,
  solo_required: true,
  world_incompatible: true,
};

/** The fields a curated member refusal may name, as the server closes them. */
const MEMBER_REFUSAL_FIELDS: Readonly<Record<string, true>> = {
  prompt: true,
  line: true,
  directive: true,
  profile: true,
  voice: true,
  "voice.accent": true,
  "voice.delivery": true,
  "voice.line": true,
};

/**
 * A curated member refusal: the field it names and the sentence this
 * response contract authorizes as member copy. Nothing else does.
 */
export function memberRefusal(
  error: unknown,
): { field: string; message: string } | undefined {
  if (
    !isApiError(error) ||
    error.status !== 422 ||
    error.code !== "member_refused"
  ) {
    return undefined;
  }
  const details = error.details;
  if (
    typeof details !== "object" ||
    details === null ||
    Array.isArray(details)
  ) {
    return undefined;
  }
  if (
    !("field" in details) ||
    typeof details.field !== "string" ||
    !Object.hasOwn(MEMBER_REFUSAL_FIELDS, details.field) ||
    !("reason_code" in details) ||
    typeof details.reason_code !== "string" ||
    !Object.hasOwn(MEMBER_REFUSAL_REASONS, details.reason_code) ||
    !("member_message" in details) ||
    typeof details.member_message !== "string"
  ) {
    return undefined;
  }
  const message = details.member_message;
  if (
    message.length > 1000 ||
    message.trim() === "" ||
    message.includes("\0")
  ) {
    return undefined;
  }
  return { field: details.field, message };
}

/** Only this response contract authorizes a server sentence as member copy. */
export function memberRefusalMessage(error: unknown): string | undefined {
  return memberRefusal(error)?.message;
}

/**
 * Decode an error response into `ApiError`.
 *
 * Tolerates three shapes, because a transport that throws while decoding an
 * error is the worst possible failure mode — it replaces a useful 403 with an
 * opaque TypeError:
 *   `{ "error": { "code", "message", "details" } }`
 *   `{ "code", "message" }`
 *   anything else, including a non-JSON body → synthesised from the status.
 */
export async function decodeApiError(res: Response): Promise<ApiError> {
  const requestId = res.headers.get("x-request-id") ?? undefined;

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }

  const envelope =
    body && typeof body === "object" && "error" in body ? body.error : body;

  let code = `http_${res.status}`;
  let message = res.statusText || `request failed with ${res.status}`;
  let details: unknown;

  if (envelope && typeof envelope === "object") {
    if ("code" in envelope && typeof envelope.code === "string") {
      code = envelope.code;
    }
    if ("message" in envelope && typeof envelope.message === "string") {
      message = envelope.message;
    }
    if ("details" in envelope) {
      details = envelope.details;
    }
  }

  return new ApiError({
    status: res.status,
    code,
    message,
    requestId,
    details,
  });
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  formData?: FormData;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  // Absolute URLs are the failure this guard exists to stop: one `https://…`
  // slipped into a call site and the request leaves the origin, drops the
  // session cookie, and starts failing CORS in exactly one environment.
  if (!path.startsWith("/v1/")) {
    throw new Error(
      `api(): expected a same-origin "/v1/..." path, received "${path}"`,
    );
  }

  const { body, formData, headers, ...rest } = opts;
  if (body !== undefined && formData !== undefined) {
    throw new Error("api(): body and formData are mutually exclusive");
  }
  const init: RequestInit = {
    ...rest,
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      ...(headers ?? {}),
    },
    body:
      formData !== undefined
        ? formData
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
  };

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (error) {
    // A navigation that cancels an in-flight request is not a transport defect,
    // and reporting it as one buries the real ones in noise.
    if (init.signal?.aborted) throw error;
    throw new ApiError({
      status: 0,
      code: "network_error",
      message: "network request failed",
    });
  }

  if (res.status === 401) unauthorizedHandler?.();

  if (!res.ok) {
    throw await decodeApiError(res);
  }

  // 204 carries no body; calling .json() on it throws.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
